<template>
    <div>
        <b-sidebar id="sidebar-backdrop" :backdrop-variant="'dark'" :title="$t('addCitation.title')" backdrop shadow
            :visible="allowAddCitation" :right="true" no-header-close no-close-on-backdrop no-close-on-esc>
            <div class="d-block text-center mt-3">
                <b-row class="align-items-center px-4">
                    <h3 class="f-18 font-weight-bold col-12 text-left">{{ $t('safetyShared.basic') }}</h3>
                    <b-col sm="12" class="mt-3">
                        <b-form-group :label="$t('addCitation.recordLabel')" class="text-left mb-0" v-if="showRecordField">
                            <b-form-input v-model="citationPayload.record_no" type="text"
                                :placeholder="$t('addCitation.recordPlaceholder')" class="unit" required></b-form-input>
                            <p v-if="errors && errors.record_no" class="field-error">
                                {{ errors.record_no[0] }}
                            </p>
                        </b-form-group>
                        <b-form-group :label="$t('addCitation.recordLabel')" class="text-left mb-0" v-else>
                            <b-form-input v-model="lastRecord" disabled type="text"
                                :placeholder="$t('addCitation.recordPlaceholder')" class="unit" required></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('addCitation.statusLabel') }}</label>
                            <b-form-select v-model="citationPayload.status" class="f-12" :options="statusOptions" @change="onStatusChange" />
                        </div>
                    </b-col>
                    <b-col sm="12" class="text-left mt-3">
                        <label for="citation-date-add-datepicker" class="state">{{ $t('addCitation.citationDateLabel')
                        }}</label>
                        <date-picker v-model="citationPayload.citation_date" :formatter="momentFormat"
                            :range-separator="' - '" @clear="citationPayload.citation_date = ''"
                            :placeholder="$t('safetyShared.selectDate')" class="date-picker-custom"
                            :teleport="'body'"></date-picker>
                        <p v-if="errors && errors.citation_date" class="field-error">
                            {{ errors.citation_date[0] }}
                        </p>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <b-form-group :label="$t('addCitation.assignedToLabel')" class="text-left select_arrow label mb-0">
                            <v-select class="bg_white w-100 f-12" v-model="citationPayload.assigned_to"
                                :options="nameOptions" item-value="id" item-text="label" :reduce="(option) => option.id"
                                @search="searchUsers">
                                <span slot="no-options">
                                    {{ $t('tasksPage.searchUser') }}
                                </span>
                            </v-select>
                        </b-form-group>
                    </b-col>
                    <b-col sm="12">
                        <hr class="mt-4" />
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('addAccident.driverLabel') }}</label>
                            <v-select class="bg_white w-100 f-12" v-model="citationPayload.driver" :options="driverOptions"
                                item-value="id" item-text="label" :reduce="(option) => option.id" @search="searchDriver">
                                <span slot="no-options">
                                    {{ $t('addAccident.searchDriver') }}
                                </span>
                            </v-select>
                            <p v-if="errors && errors.driver" class="field-error">
                                {{ errors.driver[0] }}
                            </p>
                        </div>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('addAccident.vehicleLabel') }}</label>
                            <v-select class="bg_white w-100 f-12" v-model="citationPayload.vehicle"
                                :options="vehicleOptions" item-value="id" item-text="label" :reduce="(option) => option.id"
                                @search="searchVehicle">
                                <span slot="no-options">
                                    {{ $t('addAccident.searchVehicle') }}
                                </span>
                            </v-select>
                        </div>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('addAccident.trailerLabel') }}</label>
                            <v-select class="bg_white w-100 f-12" v-model="citationPayload.trailer"
                                :options="trailerOptions" item-value="id" item-text="label" :reduce="(option) => option.id"
                                @search="searchTrailer">
                                <span slot="no-options">
                                    {{ $t('addAccident.searchTrailer') }}
                                </span>
                            </v-select>
                        </div>
                    </b-col>
                    <b-col sm="12">
                        <hr class="mt-4" />
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('addCitation.divisionLabel') }}</label>
                            <v-select class="f-12" :clearable="false" :options="allDivisions"
                                v-model="citationPayload.division" :reduce="(option) => option.id" />
                        </div>
                    </b-col>
                    <b-col sm="12">
                        <hr class="mt-4" />
                        <b-form-group :label="$t('addCitation.citationNumberLabel')" class="text-left mb-0">
                            <b-form-input v-model="citationPayload.citation_number" type="text"
                                :placeholder="$t('addCitation.citationNumberPlaceholder')" class="unit"
                                required></b-form-input>
                            <p v-if="errors && errors.citation_number" class="field-error">
                                {{ errors.citation_number[0] }}
                            </p>
                        </b-form-group>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <b-form-group :label="$t('addCitation.descriptionLabel')" class="text-left mb-0">
                            <b-form-textarea class="f-12" :placeholder="$t('addCitation.descriptionPlaceholder')" rows="3"
                                v-model="citationPayload.description"></b-form-textarea>
                        </b-form-group>
                    </b-col>
                    <b-col sm="12">
                        <div class="text-left select_arrow mt-3">
                            <label class="state">{{ $t('addCitation.issuedStateLabel') }}</label>
                            <v-select class="bg_white w-100 f-12" v-model="citationPayload.issued_state" :options="states"
                                item-value="id" item-text="label" :reduce="(option) => option.id">
                            </v-select>
                        </div>
                    </b-col>
                    <b-col sm="12">
                        <div class="text-left select_arrow mt-3">
                            <label class="state">{{ $t('addCitation.location') }}</label>
                            <b-form-input v-model="citationPayload.location" type="text"
                                :placeholder="$t('addCitation.locationPlaceholder')" class="unit" required></b-form-input>
                        </div>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <div class="text-left mb-0">
                            <label class="state">{{ $t('addCitation.fineAmountLabel') }}</label>
                            <b-form-input type="text" v-mask="amountMask"
                                :placeholder="$t('addCitation.fineAmountPlaceholder')" required
                                v-model="citationPayload.fine_amount"></b-form-input>
                            <p v-if="errors && errors.fine_amount" class="field-error">
                                {{ errors.fine_amount[0] }}
                            </p>
                        </div>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('addCitation.outOfServiceLabel') }}</label>
                            <b-form-select v-model="citationPayload.out_of_service" class="f-12"
                                :options="outOfServiceOptions" />
                        </div>
                    </b-col>
                </b-row>
                <hr class="m-4" />
                <b-row class="px-4">
                    <h3 class="f-18 font-weight-bold col-12 text-left">{{ $t('addCitation.attorneySection') }}</h3>
                    <b-col sm="12" class="text-left mb-2">
                        <b-row>
                            <b-col sm="12" class="text-left">
                                <label>{{ $t('addCitation.yesLabel') }}</label>
                                <b-form-checkbox name="check-button" class="ml-0-6rem" v-model="citationPayload.attorney"
                                    switch size="lg"></b-form-checkbox>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col sm="12" v-if="citationPayload.attorney">
                        <b-row>
                            <b-col sm="12" class="mt-3">
                                <b-form-group :label="$t('addCitation.attorneyNameLabel')" class="text-left mb-0">
                                    <b-form-input v-model="citationPayload.attorney_name" type="text"
                                        :placeholder="$t('addCitation.attorneyNamePlaceholder')"
                                        class="unit"></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col sm="12" class="mt-3">
                                <b-form-group :label="$t('addCitation.attorneyFirmLabel')" class="text-left mb-0">
                                    <b-form-input v-model="citationPayload.attorney_firm" type="text"
                                        :placeholder="$t('addCitation.attorneyFirmPlaceholder')"
                                        class="unit"></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col sm="12" class="mt-3">
                                <b-form-group :label="$t('addCitation.attorneyPhoneLabel')"
                                    class="text-left position-relative mb-2">
                                    <b-input-group size="lg" class="w-100" prepend="+1">
                                        <b-form-input v-mask="phoneMask" class="f-12"
                                            v-model="citationPayload.attorney_phone"></b-form-input>
                                    </b-input-group>
                                    <p v-if="errors && errors.attorney_phone" class="field-error mt-2">
                                        {{ errors.attorney_phone[0] }}
                                    </p>
                                </b-form-group>
                            </b-col>
                            <b-col sm="12" class="mt-3">
                                <b-form-group :label="$t('addCitation.attorneyEmailLabel')" class="text-left mb-0">
                                    <b-form-input v-model="citationPayload.attorney_email" type="text"
                                        :placeholder="$t('addCitation.attorneyEmailPlaceholder')"
                                        class="unit"></b-form-input>
                                    <p v-if="errors && errors.attorney_email" class="field-error mt-2">
                                        {{ errors.attorney_email[0] }}
                                    </p>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <hr class="m-4" />
                <b-row class="px-4 mb-7rem">
                    <h3 class="f-18 font-weight-bold col-12 text-left">{{ $t('addCitation.courtSection') }}</h3>
                    <b-col sm="12" class="text-left mb-2">
                        <b-row>
                            <b-col sm="12" class="text-left">
                                <label>{{ $t('addCitation.yesLabel') }}</label>
                                <b-form-checkbox name="check-button" class="ml-0-6rem" v-model="citationPayload.court"
                                    switch size="lg"></b-form-checkbox>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col sm="12" v-if="citationPayload.court">
                        <b-row>
                            <b-col sm="12" class="text-left mt-3">
                                <label for="court-date-add-datepicker" class="state">{{ $t('addCitation.courtDateLabel')
                                }}</label>
                                <date-picker v-model="citationPayload.court_date" :formatter="momentFormat"
                                    :range-separator="' - '" @clear="citationPayload.court_date = ''"
                                    :placeholder="$t('safetyShared.selectDate')" class="date-picker-custom"
                                    :teleport="'body'"></date-picker>
                            </b-col>
                            <b-col sm="12" class="mt-3">
                                <b-form-group :label="$t('addCitation.courtNameLabel')" class="text-left mb-0">
                                    <b-form-input v-model="citationPayload.court_name" type="text"
                                        :placeholder="$t('addCitation.courtNamePlaceholder')" class="unit"></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col sm="12">
                                <div class="text-left select_arrow mt-3">
                                    <label class="state">{{ $t('addCitation.courtLocationLabel') }}</label>
                                    <b-form-input v-model="citationPayload.court_location" type="text"
                                        :placeholder="$t('addCitation.courtLocationPlaceholder')"
                                        class="unit"></b-form-input>
                                </div>
                            </b-col>
                            <b-col sm="12" class="mt-3">
                                <div class="text-left select_arrow">
                                    <label class="state">{{ $t('addCitation.outcomeLabel') }}</label>
                                    <b-form-select v-model="citationPayload.outcome" class="f-12"
                                        :options="outcomeOptions" />
                                    <p v-if="citationPayload.status === 'Closed' && !['Overturned', 'Convicted'].includes(citationPayload.outcome)"
                                        class="field-error">{{ $t('addCitation.outcomePendingClose') }}</p>
                                </div>
                            </b-col>
                            <b-col sm="12" class="text-left mt-3">
                                <label for="resolution-date-add-datepicker" class="state">{{
                                    $t('addCitation.resolutionDateLabel')
                                }}</label>
                                <date-picker v-model="citationPayload.resolution_date" :formatter="momentFormat"
                                    :range-separator="' - '" @clear="citationPayload.resolution_date = ''"
                                    :placeholder="$t('safetyShared.selectDate')" class="date-picker-custom"
                                    :teleport="'body'"></date-picker>
                                <p v-if="citationPayload.status === 'Closed' && !citationPayload.resolution_date"
                                    class="field-error">{{ $t('addCitation.resolutionDateRequiredClose') }}</p>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <div class="sidebar-footer position-fixed">
                    <div class="text-right modal-footer border-0 p-0">
                        <b-button class="mx-0 mr-3 h-40px w-100px" @click="resetPopup()" variant="secondary">{{
                            $t('safetyShared.cancel') }}</b-button>
                        <b-button class="mx-0 h-40px px-2 mr-3" @click="onAddNewCitation(true)" variant="outline-primary">{{
                            $t('safetyShared.saveAndOpen') }}</b-button>
                        <b-button class="mx-0 h-40px w-100px" @click="onAddNewCitation()" variant="primary">{{
                            $t('safetyShared.save') }}</b-button>
                    </div>
                </div>
            </div>
        </b-sidebar>
    </div>
</template>
<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions, mapGetters } from "vuex";
import * as moment from "moment-timezone";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { checkEmail, checkValueExists, checkPhoneFormat } from "@/services/helper";

const amountMask = createNumberMask({
    prefix: "",
    allowDecimal: true,
    includeThousandsSeparator: true,
});
export default {
    components: { vSelect, DatePicker },
    name: "AddCitation",
    props: {
        allowAddCitation: {
            type: Boolean,
            default: false
        },
    },
    computed: {
        ...mapGetters([
            "userCompany",
            "allEquipments",
            "allDrivers",
            "allCountries",
            "divisions",
            "allTeamMembers"
        ]),
        phoneMask() {
            return this.citationPayload.attorney_phone?.length > 0
                ? [
                    /\d/, /\d/, /\d/, " ",
                    /\d/, /\d/, /\d/, " ",
                    /\d/, /\d/, /\d/, /\d/
                ]
                : false; // Allows empty input
        },
        statusOptions() {
            return [
                { value: 'New', text: this.$t('safetyShared.statusNew') },
                { value: 'Open', text: this.$t('safetyShared.statusOpen') },
                { value: 'Closed', text: this.$t('safetyShared.statusClosed') },
                { value: 'Urgent', text: this.$t('safetyShared.statusUrgent') },
            ];
        },
        boolFilterOptions() {
            return [
                { value: '', text: '' },
                { value: true, text: this.$t('safetyShared.yes') },
                { value: false, text: this.$t('safetyShared.no') },
            ];
        },
        outcomeOptions() {
            return [
                { value: '', text: '-' },
                { value: 'Pending', text: this.$t('addCitation.outcomePending') },
                { value: 'Overturned', text: this.$t('addCitation.outcomeOverturned') },
                { value: 'Convicted', text: this.$t('addCitation.outcomeConvicted') },
            ]
        },
        outOfServiceOptions() {
            return [
                { value: null, text: '-' },
                { value: true, text: this.$t('safetyShared.yes') },
                { value: false, text: this.$t('safetyShared.no') },
            ];
        },
        allDivisions() {
            return this.divisions.map((item) => {
                return {
                    id: item.id,
                    label: item.division,
                }
            })
        },
        isCloseInvalid() {
            // A citation with court involvement can't be "Closed" until the outcome is
            // resolved (Overturned/Convicted) and a resolution date is set.
            if (this.citationPayload.status !== 'Closed') return false;
            if (!this.citationPayload.court) return false;
            const resolvedOutcome = ['Overturned', 'Convicted'].includes(this.citationPayload.outcome);
            return !resolvedOutcome || !this.citationPayload.resolution_date;
        },
        closeInvalidReason() {
            // Spells out exactly why the citation can't be closed.
            const needsOutcome = !['Overturned', 'Convicted'].includes(this.citationPayload.outcome);
            const needsDate = !this.citationPayload.resolution_date;
            if (needsOutcome && needsDate) return this.$t('addCitation.citationCloseInvalid');
            if (needsOutcome) return this.$t('addCitation.outcomePendingClose');
            return this.$t('addCitation.resolutionDateRequiredClose');
        },
    },
    data() {
        return {
            errors: {},
            previousStatus: 'New',
            states: [],
            amountMask,
            nameOptions: [],
            lastRecord: null,
            showRecordField: false,
            claimNumbers: [],
            accidentID: null,
            checkEmail,
            checkValueExists,
            checkPhoneFormat,
            momentFormat: {
                stringify: (date) => {
                    return date ? moment(date).format('ll') : ''
                },
                parse: (value) => {
                    if (!value) return null
                    const formats = [
                        'll', 'LL',
                        'MMM D, YYYY', 'MMMM D, YYYY',
                        'M/D/YYYY', 'MM/DD/YYYY',
                        'M-D-YYYY', 'MM-DD-YYYY',
                        'YYYY-MM-DD', 'YYYY/MM/DD',
                        'D MMM YYYY', 'D MMMM YYYY',
                        'D/M/YYYY', 'DD/MM/YYYY',
                    ]
                    const m = moment(value, formats, true)
                    if (m.isValid()) return m.toDate()
                    const lenient = moment(value)
                    return lenient.isValid() ? lenient.toDate() : null
                }
            },
            citationPayload: {
                // Basic
                status: "New",
                division: null,
                driver: null,
                vehicle: null,
                trailer: null,
                assigned_to: null,

                // Citation details
                citation_number: "",
                citation_date: null,
                description: "",
                issued_state: null,
                location: "",
                fine_amount: "",
                out_of_service: null,

                // Attorney
                attorney: false,
                attorney_name: "",
                attorney_firm: "",
                attorney_phone: "",
                attorney_email: "",

                // Court
                court: false,
                court_date: null,
                court_name: "",
                court_location: "",
                outcome: "",
                resolution_date: null,
            },
            driverOptions: [],
            vehicleOptions: [],
            trailerOptions: [],
        }
    },
    methods: {
        ...mapActions([
            "getFilteredEquipments",
            "getFilteredDrivers",
            "setLoader",
            "addCitation",
            "setNotification",
            "getAllClaims",
            "getAllCountries",
            "getLastRecord",
            "getAllTeamMembers"
        ]),
        async searchUsers(searchText) {
            if (searchText) {
                let options = []
                let url = `page=1&page_size=20&name=${searchText}`;
                await this.getAllTeamMembers(url);
                this.allTeamMembers.map((member) => {
                    options.push({
                        id: `${member.id}`,
                        label: member.user.first_name + ' ' + member.user.last_name,
                    });
                });
                this.nameOptions = options
                this.nameOptions = [...new Map(this.nameOptions.map(item => [item['id'], item])).values()]
            }
        },
        checkNullValues(payload) {
            let nullKeys = [];
            for (let [key, value] of Object.entries(payload)) {
                if (!value) {
                    nullKeys.push(key);
                }
            }
            return nullKeys;
        },
        onStatusChange(newVal) {
            // Block switching to "Closed" while the court status is unresolved — revert
            // and show a red alert explaining exactly what's missing.
            if (newVal === 'Closed' && this.isCloseInvalid) {
                this.setNotification({
                    msg: this.closeInvalidReason,
                    type: "error",
                    color: "red",
                });
                this.$nextTick(() => {
                    this.citationPayload.status = this.previousStatus;
                });
                return;
            }
            this.previousStatus = newVal;
        },
        validateClose() {
            if (!this.isCloseInvalid) return true;
            this.setNotification({
                msg: this.closeInvalidReason,
                type: "error",
                color: "red",
            });
            return false;
        },
        async onAddNewCitation(openCitation = false) {
            if (!this.validateClose()) return;
            try {
                if (this.citationPayload.attorney_email) {
                    if (!this.checkEmail(this.citationPayload.attorney_email)) {
                        throw {
                            attorney_email: [this.$t('users.wrongFormat')],
                        };
                    }
                }
                if (this.citationPayload.attorney_phone) {
                    if (!this.checkPhoneFormat(this.citationPayload.attorney_phone)) {
                        throw {
                            attorney_phone: [this.$t('users.wrongFormat')],
                        };
                    }
                }
                const nullKeys = this.checkNullValues({
                    citation_date: this.citationPayload.citation_date,
                    driver: this.citationPayload.driver,
                    ...this.showRecordField ? { record_no: this.citationPayload.record_no } : {},
                });
                if (nullKeys.length === 0) {
                    this.setLoader(true);
                    if (!this.showRecordField) delete this.citationPayload.record_no
                    const record = await this.addCitation({
                        ...{
                            ...this.citationPayload, citation_date: this.citationPayload.citation_date ? moment(this.citationPayload.citation_date).format('YYYY-MM-DD') : null,
                            court_date: this.citationPayload.court_date ? moment(this.citationPayload.court_date).format('YYYY-MM-DD') : null,
                            resolution_date: this.citationPayload.resolution_date ? moment(this.citationPayload.resolution_date).format('YYYY-MM-DD') : null,
                            fine_amount: this.citationPayload.fine_amount ? parseFloat(this.citationPayload.fine_amount.toString().replace(/,/g, '')) : null,
                        }
                    }
                    );
                    await this.setNotification({
                        msg: this.$t('addCitation.added'),
                        type: "success",
                        color: "green",
                    });
                    this.setLoader(false);
                    if (openCitation) this.$emit("citationAdded", record)
                    else this.$emit("citationAdded", null)
                    this.resetPopup()
                }
                else {
                    let error = {};
                    nullKeys.forEach((x) => {
                        error[x] = [this.$t('safetyShared.fieldRequired')];
                    });
                    throw error;
                }
            }
            catch (error) {
                if (typeof error === "object") {
                    this.errors = error;
                    // eslint-disable-next-line no-ex-assign
                }
                this.setLoader(false);
            }

        },
        resetPopup() {
            this.previousStatus = 'New'
            this.citationPayload = {
                status: "New",
                division: null,
                driver: null,
                vehicle: null,
                trailer: null,
                assigned_to: null,
                citation_number: "",
                citation_date: null,
                description: "",
                issued_state: null,
                location: "",
                fine_amount: "",
                out_of_service: null,
                attorney: false,
                attorney_name: "",
                attorney_firm: "",
                attorney_phone: "",
                attorney_email: "",
                court: false,
                court_date: null,
                court_name: "",
                court_location: "",
                outcome: "",
                resolution_date: null,
            }
            this.errors = {}
            this.driverOptions = []
            this.vehicleOptions = []
            this.trailerOptions = []
            this.$emit('close');
        },
        async searchDriver(searchText) {
            if (searchText) {
                let options = []
                const driverRecords = await this.getFilteredDrivers(searchText);
                driverRecords.forEach((x) => {
                    options.push({
                        id: x.id,
                        label: x.name,
                    });
                });
                this.driverOptions = this.driverOptions.concat(options)
                this.driverOptions = [...new Map(this.driverOptions.map(item => [item['id'], item])).values()]
            }
        },
        async searchVehicle(searchText) {
            if (searchText) {
                let options = [];
                const vehicleRecords = await this.getFilteredEquipments({ unit_no: searchText, type: "Vehicle" });
                vehicleRecords.forEach((x) => {
                    options.push({
                        id: x.id,
                        label: x.unit_no,
                    });
                });
                this.vehicleOptions = this.vehicleOptions.concat(options)
                this.vehicleOptions = [...new Map(this.vehicleOptions.map(item => [item['id'], item])).values()]
            }
        },
        async searchTrailer(searchText) {
            if (searchText) {
                let options = [];
                const trailerRecords = await this.getFilteredEquipments({ unit_no: searchText, type: "Trailer" });
                trailerRecords.forEach((x) => {
                    options.push({
                        id: x.id,
                        label: x.unit_no,
                    });
                });
                this.trailerOptions = this.trailerOptions.concat(options)
                this.trailerOptions = [...new Map(this.trailerOptions.map(item => [item['id'], item])).values()]
            }
        },
        async getCountries() {
            await this.getAllCountries();
            const countries = this.allCountries.map((item) => ({
                id: item.id,
                label: item.name,
            }));
            this.getStates(countries[0]);
        },
        getStates(selectedOption) {
            this.states = this.allCountries
                .filter((country) => country.id === selectedOption.id)[0]
                .state.map((item) => ({
                    id: item.id,
                    label: item.name,
                }));
            this.citationPayload.state = this.states.filter(
                (state) => state.label === "-"
            )[0].id;
            this.citationPayload.report_state = this.states.filter(
                (state) => state.label === "-"
            )[0].id;
        }
    },
    watch: {
        async allowAddCitation() {
            if (this.allowAddCitation) {
                this.setLoader(true)
                const lastItemRecord = await this.getLastRecord({
                    company_id: this.userCompany,
                    query: `model_name=ticket`
                })
                if (lastItemRecord) this.lastRecord = lastItemRecord + 1;
                else this.showRecordField = true
                this.setLoader(false)
            }
        },
        'citationPayload.court'(val) {
            // Default outcome to "Pending Court" when court is involved, else clear it.
            if (val) {
                if (!this.citationPayload.outcome) this.citationPayload.outcome = 'Pending'
            } else {
                this.citationPayload.outcome = ''
            }
        },
    },
    async mounted() {
        await this.getCountries()
    },
}
</script>